/**
 * OpportunityPriceChart — the L2 finding page's TWO charts (Lane 47
 * Amendment A1; Consult #4 resolved by Ryan 2026-07-02).
 *
 * Chart 1 "Price paid over time": a visual CLONE of the MaterialDossier
 * "Price Over Time by Supplier" chart (MaterialDossier.js ~670-775 + helpers
 * ~1514-1551) — same palette, axis rules, tooltip, legend, outlier
 * treatment (capped points drawn as amber open circles + the collapsible
 * "excluded from scale" footnote) — applied to the page's per-family
 * windowMonths slice (dispersion 24 / volume + same-day 18, A3 item 3),
 * plus the savings-window marker and (volume findings) a
 * green dot on the frozen best-price purchase. Per the standing 2B decision
 * this remains a COPIED component: MaterialDossier.js is untouched; the
 * code-level unification is filed BY NAME in the dossier-callout lane.
 * One deliberate, Ryan-locked deviation (Consult #4 resolution): multiple
 * same-day purchases by one supplier plot the day's LOWEST price (the
 * dossier's overwrite keeps the last) — pinned by test.
 *
 * Chart 2 "Your price vs the market" (A2.6): the market line is the
 * SUPPLIER's blended market index — the EXACT series the supplier dossier
 * renders (supplier-price-impact ppi_data_points; ONE source, the two pages
 * can never disagree) — re-anchored with the material's monthly-average
 * line at the FIRST COMMON in-window month (Consult #4: no phantom gap;
 * Consult #5: pct->level conversion happens before any filtering, and the
 * caption narrates the LAST NON-NULL point of each series). Gate: blend
 * data present + a common anchor exists; every degraded state (no profile,
 * insufficient data, no common month) falls to the quiet note.
 *
 * Lane 1358 (Ryan, 2026-09-30, from a mockup he approved): the two charts are
 * two SEPARATE, EQUAL blocks side by side (each half the page; they stack on a
 * narrow screen). "Equal" is built in, not hoped for: both use the same chart
 * height, margins and y-axis width, both captions reserve the same two lines,
 * and both keys are drawn as HTML BELOW the chart (not as a recharts <Legend>,
 * which steals plot height in proportion to how many lines it wraps to), so
 * the two plot areas are the same size whatever the supplier count. The
 * Blended PPI paragraph that used to sit under chart 2 is now the hover note
 * of the gray "Blended PPI" key item (hover, keyboard focus, or a tap), with
 * the same words. When there is no market line, chart 1 spans the full width
 * and the quiet notes render under it as before. Layout only: no series,
 * domain, capping, tooltip or figure changed. The block classes
 * (opp-chartpair / opp-chartcard / opp-key*) live in Opportunities.linear.css;
 * this component renders only on the finding page.
 */

import React, { useState, useId, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine,
} from 'recharts';
import { colors } from '../SharedComponents';
import {
  buildDossierRows, buildIndexedComparisonRows, toLocalEpoch, monthsBefore,
  normalizeSupplierKey,
} from '../../utils/opportunityChart';

// A3 item 3 (Ryan decision (a), 2026-07-02) supersedes plan §2D's fixed 18:
// the window arrives as a per-family prop from the finding page (dispersion
// 24 — the engine's own typical window; volume/same-day 18). Still no toggle.
const DEFAULT_WINDOW_MONTHS = 18;
const SAVINGS_WINDOW_MONTHS = 12;  // the marker agreeing with the math panel

// ── Dossier-clone helpers (copied verbatim from MaterialDossier.js ~1514-1551;
// 2B: copied, not extracted — unification filed in the dossier-callout lane) ──

const SUPPLIER_COLORS = ['#2251FF', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#BE185D', '#4338CA'];

function formatChartPrice(val) {
  if (val == null) return '';
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
  if (val >= 1) return `$${val.toFixed(2)}`;
  return `$${val.toFixed(4)}`;
}

function PriceTrendTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #E2E5EA', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 600, marginBottom: '6px', color: '#111827' }}>{label}</div>
      {payload.map((entry, i) => {
        // Bug B (2026-07-03): list EVERY PO on this day for this supplier
        // (price + its own quantity), sorted lowest-first, so the tooltip
        // matches the PO table one-to-one instead of showing only the plotted
        // (lowest) price. A SINGLE-PO day renders exactly as before (name:
        // plotted price + summed qty) -- no visible change. A capped PO shows
        // its real UNCAPPED price beside the "excluded from scale" marker.
        //
        // Lane 824: this label used to read "flagged unusual". `is_outlier`
        // here is the price-history endpoint's IQR statistic, NOT the finding's
        // own evidence, and the two disagree (1 of 26 staging findings) -- so
        // that wording made the chart a second, contradicting answer to "which
        // PO is unusual" on a page whose whole job is to answer it once. The
        // marker is kept and re-worded rather than deleted, because the amber
        // ring at the dot (line ~245) is a REAL and honest claim: that point is
        // plotted at a capped value and held out of the y-domain. It now says
        // exactly that, in the same words as the footnote below the chart, and
        // the word "unusual" appears nowhere on this page except the finding's
        // own frozen evidence.
        const pos = (entry.payload?._pos?.[entry.name] || [])
          .slice()
          .sort((a, b) => a.price - b.price);
        const dot = (
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: entry.color, display: 'inline-block' }} />
        );
        const qtyText = (q) => (q ? (
          <span style={{ color: '#6B7280' }}>&middot; {q.toLocaleString()} units</span>
        ) : null);
        const flagText = (isOut) => (isOut ? (
          <span style={{ color: '#D97706' }}>&middot; excluded from scale</span>
        ) : null);

        if (pos.length <= 1) {
          // Unchanged single-PO line (plotted value + summed day units).
          const dayQty = entry.payload?._qty?.[entry.name];
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
              {dot}
              <span style={{ color: '#6B7280' }}>{entry.name}:</span>
              <span style={{ fontWeight: 500, fontFamily: 'monospace' }}>{formatChartPrice(entry.value)}</span>
              {qtyText(dayQty)}
            </div>
          );
        }
        // Multiple POs that day: supplier header + one line per PO.
        return (
          <div key={i} style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
              {dot}
              <span style={{ color: '#6B7280' }}>{entry.name}</span>
            </div>
            {pos.map((po, j) => (
              <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '14px', marginBottom: '1px' }}>
                <span style={{ fontWeight: 500, fontFamily: 'monospace' }}>{formatChartPrice(po.price)}</span>
                {qtyText(po.quantity)}
                {flagText(po.is_outlier)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

const blockLabelStyle = {
  fontSize: '11px', textTransform: 'uppercase', fontWeight: 600,
  letterSpacing: '0.06em', color: colors.textMuted, margin: '0 0 4px',
};
// Lane 1358: the caption under each title reserves two lines (it is the one
// piece of text above the plot whose length differs between the charts), so
// both plots start at the same height and line up side by side.
const subStyle = {
  fontSize: '12px', color: colors.secondaryText, margin: '0 0 10px',
  lineHeight: 1.5, minHeight: '3em',
};

// Lane 1358: ONE geometry for both charts so the two blocks match.
const CHART_HEIGHT = 260;
const CHART_MARGIN = { top: 5, right: 20, bottom: 5, left: 10 };
const Y_AXIS_WIDTH = 65;
const PPI_KEY_COLOR = '#9CA3AF';

// The recharts default legend glyph for a line series (a short line through a
// hollow dot), drawn as HTML so the key sits outside the plot area.
function KeyGlyph({ color, dashed }) {
  return (
    <svg width="14" height="10" viewBox="0 0 14 10" aria-hidden="true" focusable="false">
      <line x1="0" y1="5" x2="14" y2="5" stroke={color} strokeWidth="2" strokeDasharray={dashed ? '3 2' : undefined} />
      <circle cx="7" cy="5" r="2.5" fill="#fff" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

function KeyItem({ color, dashed, label }) {
  return (
    <span className="opp-keyitem" style={{ color }}>
      <KeyGlyph color={color} dashed={dashed} />
      <span>{label}</span>
    </span>
  );
}

// The gray "Blended PPI" key item. Its note shows while the pointer is over
// the item or the note itself, while the item has keyboard focus, or after a
// tap (touch screens have no hover). Hover and focus are tracked separately,
// so moving the mouse away does not hide the note from a keyboard user, and a
// transparent bridge (CSS) covers the gap so the pointer can move into the
// note to read it. Escape (anywhere on the page) hides it until the next
// hover, focus or tap. The
// note is a role="tooltip" tied to the button with aria-describedby.
function PpiKeyItem({ label, note }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const noteId = useId();
  const open = !dismissed && (hovered || focused);
  // Escape must close the note however it was opened, including by hover
  // alone (the button never had focus), so listen on the document while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDismissed(true); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <span
      className="opp-keytip"
      onMouseEnter={() => { setHovered(true); setDismissed(false); }}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        className="opp-keyitem opp-keybtn"
        style={{ color: PPI_KEY_COLOR }}
        aria-describedby={open ? noteId : undefined}
        onFocus={() => { setFocused(true); setDismissed(false); }}
        onBlur={() => setFocused(false)}
        onClick={() => { setFocused(true); setDismissed(false); }}
      >
        <KeyGlyph color={PPI_KEY_COLOR} dashed />
        <span>{label}</span>
      </button>
      {open && (
        <span role="tooltip" id={noteId} className="opp-keypop">{note}</span>
      )}
    </span>
  );
}

const capStyle = { fontSize: '11.5px', color: colors.textMuted, marginTop: '8px', lineHeight: 1.5 };

function OpportunityPriceChart({
  priceHistory,          // raw records from /api/materials/{m}/price-history
  priceImpact,           // body of /api/analytics/supplier-price-impact/{s}, or null
  supplierDisplay,       // the finding's supplier
  bestPoDate,            // frozen best-price PO date (volume) or null
  bestPrice,             // frozen best price (volume) or null
  windowMonths = DEFAULT_WINDOW_MONTHS,   // per-family evidence window (A3 item 3)
  asOf = new Date(),     // window anchor: snapshot date for same-day (Bug A), today otherwise
}) {
  const [outlierFootnoteOpen, setOutlierFootnoteOpen] = useState(false);

  const { lineData, suppliers, windowOutliers, yDomain } =
    buildDossierRows(priceHistory, asOf, windowMonths);

  // Chart 2 (A2.6): the supplier's blended market series; every degraded
  // state (no profile / insufficient data / no common anchor) -> null ->
  // the quiet note. NO recomputation of the blend — one source.
  const comparison = buildIndexedComparisonRows(
    priceHistory, priceImpact?.ppi_data_points, supplierDisplay, asOf,
    windowMonths);

  // A3 item 4 (Ryan round-3 QA): name WHICH PPI the market line is, the way
  // the supplier dossier does — the legend says "Blended PPI" and the caption
  // lists the blend's composition (series_breakdown names + weights, rendered
  // verbatim from the same response the dossier reads).
  const blendSeries = priceImpact?.ppi_blend?.series_breakdown || [];

  // Savings-window marker: the first plotted row on/after the 12-month line.
  // Category-axis note: ReferenceLine matches the FIRST row carrying this
  // 'MMM YY' label, so the marker is month-granular by design (a same-month
  // earlier purchase shifts it by days at most on the chart's window-length
  // axis).
  const savingsStart = monthsBefore(asOf, SAVINGS_WINDOW_MONTHS);
  const savingsRow = lineData.find((r) => toLocalEpoch(r.date) >= savingsStart);

  const hlKey = normalizeSupplierKey(supplierDisplay);
  const bestEpoch = bestPoDate ? toLocalEpoch(bestPoDate) : null;
  // The green best-price dot renders only when a plotted point actually
  // matches the frozen anchor (same date, same value, the finding's
  // supplier); the caption below must agree — never claim a dot that could
  // not be drawn (/review fix, 2026-07-02).
  const bestDotVisible = bestEpoch !== null
    && bestPrice !== null && bestPrice !== undefined
    && lineData.some((row) => toLocalEpoch(row.date) === bestEpoch
      && suppliers.some((s) => normalizeSupplierKey(s) === hlKey
        && row[s] === Number(bestPrice)));

  // BP-c: profile-based wording (the source is the supplier's market blend,
  // not the material-category mapping anymore).
  const noBenchmarkNote = (
    <div style={capStyle}>
      No reliable market benchmark is available for this supplier.
    </div>
  );

  // A2.4 / BP-b: the caption narrates the LAST NON-NULL plotted point of
  // each series — nothing newly computed, purely the chart's own endpoints.
  const lastNonNull = (rows, key) => {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (rows[i][key] !== null && rows[i][key] !== undefined) return rows[i][key];
    }
    return null;
  };
  const fmtPct = (v) => `${v >= 0 ? 'up ' : 'down '}${Math.abs(v).toFixed(1)}%`;

  if (lineData.length === 0) {
    return (
      <div className="opp-panelcard">
        <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, padding: '24px 0' }}>
          No purchases in the last {windowMonths} months to chart.
        </div>
        {noBenchmarkNote}
      </div>
    );
  }

  const hasComparison = Boolean(comparison && comparison.rows.length >= 2);

  // ── Chart 1: dossier clone ──
  const priceChart = (
    <div className="opp-panelcard opp-chartcard">
      <h3 style={blockLabelStyle}>Price paid over time</h3>
      <p style={subStyle}>
        Every purchase of this material, last {windowMonths} months.
      </p>
      {lineData.length < 2 ? (
        <div style={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
          Not enough data points to show trend
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={lineData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} />
              <YAxis domain={yDomain} tickFormatter={formatChartPrice} tick={{ fontSize: 11, fill: '#6B7280' }} width={Y_AXIS_WIDTH} />
              <Tooltip content={<PriceTrendTooltip />} />
              {savingsRow && (
                <ReferenceLine
                  x={savingsRow.label}
                  stroke="#8A94A6"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  label={{ value: 'savings window', position: 'insideTopLeft', fontSize: 10, fill: '#8A94A6' }}
                />
              )}
              {suppliers.map((supplier, idx) => {
                const color = SUPPLIER_COLORS[idx % SUPPLIER_COLORS.length];
                const isFindingSupplier = normalizeSupplierKey(supplier) === hlKey;
                return (
                  <Line
                    key={supplier}
                    type="monotone"
                    dataKey={supplier}
                    name={supplier}
                    stroke={color}
                    strokeWidth={2}
                    dot={(props) => {
                      // Lane 1358: a row where THIS supplier did not buy has
                      // no value, and recharts passes cy=null for it. A circle
                      // with no cy is drawn at y=0, i.e. a stray dot along the
                      // top edge of the chart for every such row on any
                      // multi-supplier material (measured on the live build
                      // before this fix). Draw nothing for a missing point.
                      if (props.cy == null || props.value == null) {
                        return <g key={props.index} />;
                      }
                      const row = lineData[props.index];
                      const isOut = row?._outliers?.[supplier] != null;
                      if (isOut) {
                        return <circle key={props.index} cx={props.cx} cy={props.cy} r={4} fill="none" stroke="#D97706" strokeWidth={2} />;
                      }
                      // Lane 47: the frozen best-price purchase gets the
                      // green dot (volume findings; the finding's supplier).
                      const isBest = isFindingSupplier
                        && bestEpoch !== null
                        && bestPrice !== null && bestPrice !== undefined
                        && row && toLocalEpoch(row.date) === bestEpoch
                        && row[supplier] === Number(bestPrice);
                      if (isBest) {
                        return <circle key={props.index} cx={props.cx} cy={props.cy} r={5} fill="#059669" stroke="#fff" strokeWidth={1.5} />;
                      }
                      return <circle key={props.index} cx={props.cx} cy={props.cy} r={3} fill={color} strokeWidth={2} />;
                    }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
          <div className="opp-chartkey">
            {suppliers.map((supplier, idx) => (
              <KeyItem key={supplier} color={SUPPLIER_COLORS[idx % SUPPLIER_COLORS.length]} label={supplier} />
            ))}
          </div>
          {windowOutliers.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div
                onClick={() => setOutlierFootnoteOpen(!outlierFootnoteOpen)}
                style={{ fontSize: '12px', color: colors.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span style={{ fontSize: '10px' }}>{outlierFootnoteOpen ? '▼' : '▶'}</span>
                {windowOutliers.length} data point{windowOutliers.length !== 1 ? 's' : ''} excluded from scale
              </div>
              {outlierFootnoteOpen && windowOutliers.map((o, i) => (
                <div key={i} style={{ fontSize: '12px', color: colors.textMuted, marginLeft: '14px', marginTop: '2px', fontFamily: 'monospace' }}>
                  {o.supplier}: {formatChartPrice(o.price)} on {o.date}
                </div>
              ))}
            </div>
          )}
          {bestDotVisible && (
            <div style={capStyle}>
              The green dot marks the best-price purchase this finding is
              anchored on.
            </div>
          )}
        </>
      )}
      {/* No market line: chart 1 is the only block (full width) and the quiet
          notes stay under it, unchanged. */}
      {!hasComparison && (
        <>
          {noBenchmarkNote}
          <div style={capStyle}>
            Chart shows current purchase history; the figures above are frozen
            as of the snapshot date.
          </div>
        </>
      )}
    </div>
  );

  if (!hasComparison) {
    return <div className="opp-chartpair solo">{priceChart}</div>;
  }

  // /review fix (A3): the blend's series values AND spend weights are computed
  // over the REQUESTED window, and this page's window (18 or 24 months) differs
  // from the supplier page's default (12) — so the note names the same-index
  // relationship without claiming the two renders are identical. Lane 1358:
  // these are the exact words of the old caption, now shown as the hover note
  // of the "Blended PPI" key item instead of a paragraph under the chart.
  const ppiNote = (
    <>
      The market line is {supplierDisplay}&apos;s Blended PPI
      {blendSeries.length > 0 && (
        <> ({blendSeries.map((s) => `${s.series_name} ${s.spend_weight_pct}%`).join(' · ')})</>
      )}
      {' '}- the same market index the supplier&apos;s page shows,
      computed here over this chart&apos;s {windowMonths}-month window.
      It tracks that supplier&apos;s product mix, not this exact
      item&apos;s price. Chart shows current purchase history; the
      figures above are frozen as of the snapshot date.
    </>
  );

  // ── Chart 2: supplier-dossier-style indexed comparison, trust-gated ──
  const marketChart = (
    <div className="opp-panelcard opp-chartcard">
      <h3 style={blockLabelStyle}>Your price vs the market</h3>
      <p style={subStyle}>
        {(() => {
          const yourPct = lastNonNull(comparison.rows, 'your_price_index');
          const marketPct = lastNonNull(comparison.rows, 'ppi_index');
          if (yourPct === null || marketPct === null) {
            return `Both lines start at 0% - % change since ${comparison.anchorMonth}.`;
          }
          return `Your price for this material is ${fmtPct(yourPct)} since `
            + `${comparison.anchorMonth}; the market is ${fmtPct(marketPct)}.`;
        })()}
      </p>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart data={comparison.rows} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
          <XAxis dataKey="monthLabel" tick={{ fontSize: 11, fill: '#6B7280' }} />
          <YAxis
            tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
            tick={{ fontSize: 11, fill: '#6B7280' }}
            width={Y_AXIS_WIDTH}
            domain={['auto', 'auto']}
          />
          <Tooltip
            formatter={(value, name) => [
              value != null ? `${value >= 0 ? '+' : ''}${Number(value).toFixed(1)}%` : 'N/A',
              name,
            ]}
          />
          <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="3 3" strokeWidth={1} />
          <Line
            type="monotone"
            dataKey="your_price_index"
            name={`${supplierDisplay} monthly avg price (% change)`}
            stroke="#2251FF"
            strokeWidth={2}
            dot={{ r: 3, fill: '#2251FF' }}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="ppi_index"
            name="Blended PPI (% change)"
            stroke={PPI_KEY_COLOR}
            strokeWidth={2}
            strokeDasharray="6 3"
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="opp-chartkey">
        <PpiKeyItem label="Blended PPI (% change)" note={ppiNote} />
        <KeyItem color="#2251FF" label={`${supplierDisplay} monthly avg price (% change)`} />
      </div>
    </div>
  );

  return (
    <div className="opp-chartpair">
      {priceChart}
      {marketChart}
    </div>
  );
}

export default OpportunityPriceChart;
